import { CURRENCY, MIN_PLAYERS, PLAYER_COLORS } from "@grundbesitz/shared";
import type { GameOptions, PlayerColorId, RoomMember, TokenId } from "@grundbesitz/shared";

export const CASH_MIN = 100;
export const CASH_MAX = 100_000;
export const CASH_STEP = 100;
export const CASH_PRESETS = [1000, 1500, 2000, 3000, 5000] as const;

export const TIME_LIMIT_MIN = 10;
export const TIME_LIMIT_MAX = 600;
export const TIME_LIMIT_STEP = 10;
export const TIME_LIMIT_DEFAULT = 60;

export const TIMER_CHOICES = [0, 30, 60, 90] as const;

export function clampInt(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, Math.round(value)));
}

/** Reads a whole number typed by a player ("1.500", "1 500", "1500"). Returns null for anything else. */
export function parseWholeNumber(text: string): number | null {
  const compact = text.replace(/[.\s ']/g, "");
  if (!/^\d{1,9}$/.test(compact)) return null;
  return Number(compact);
}

/** Members that hold up the start: humans (not the host) who are not ready yet. */
export function waitingFor(members: RoomMember[]): RoomMember[] {
  return members.filter((m) => !m.isBot && !m.isHost && !m.ready);
}

function joinNames(names: string[]): string {
  if (names.length <= 3) return names.join(", ");
  return `${names.slice(0, 3).join(", ")} und ${names.length - 3} weitere`;
}

/** Why the host cannot start yet, or null when the game can start. Shown next to the disabled button. */
export function startBlocker(members: RoomMember[]): string | null {
  if (members.length < MIN_PLAYERS) return `Mindestens ${MIN_PLAYERS} Spieler nötig.`;
  const host = members.find((m) => m.isHost);
  if (host && (!host.token || !host.color)) return "Wähle zuerst Figur und Farbe.";
  const waiting = waitingFor(members);
  if (waiting.length > 0) return `Warte auf: ${joinNames(waiting.map((m) => m.name))}`;
  return null;
}

/** Why the viewer cannot mark themselves ready, or null. */
export function readyBlocker(me: RoomMember): string | null {
  if (!me.token || !me.color) return "Wähle zuerst Figur und Farbe.";
  return null;
}

/** Tokens taken by other members, keyed by token. */
export function takenTokens(members: RoomMember[], meId: string | null): Map<TokenId, RoomMember> {
  const out = new Map<TokenId, RoomMember>();
  for (const m of members) if (m.token && m.id !== meId) out.set(m.token, m);
  return out;
}

export function takenColors(members: RoomMember[], meId: string | null): Map<PlayerColorId, RoomMember> {
  const out = new Map<PlayerColorId, RoomMember>();
  for (const m of members) if (m.color && m.id !== meId) out.set(m.color, m);
  return out;
}

export function colorName(id: PlayerColorId | null): string {
  return PLAYER_COLORS.find((c) => c.id === id)?.name ?? "Farbe offen";
}

export function activeHouseRules(options: GameOptions): string[] {
  const out: string[] = [];
  if (options.houseRules.freeParkingPot) out.push("Frei-Parken-Topf");
  if (options.houseRules.doubleGoOnLanding) out.push("Doppeltes LOS-Geld bei direkter Landung");
  if (options.houseRules.noAuction) out.push("Keine Versteigerung bei Ablehnung");
  if (options.houseRules.buildAfterAllStreetsSold) out.push("Bauen erst, wenn alle Straßen verkauft sind");
  if (options.houseRules.dealStreets) out.push("Straßen werden zu Beginn verteilt");
  return out;
}

export function formatAmount(n: number): string {
  return `${CURRENCY} ${n.toLocaleString("de-DE")}`;
}

/** Read-only summary of the options, for everyone but the host. */
export function summarizeOptions(o: GameOptions): { label: string; value: string }[] {
  const rules = activeHouseRules(o);
  return [
    { label: "Miete", value: o.rentMode === "AUTO" ? "Automatisch" : "Regeltreu einfordern" },
    { label: "Zug-Timer", value: o.turnTimerSeconds === 0 ? "Aus" : `${o.turnTimerSeconds} Sekunden` },
    { label: "Startkapital", value: formatAmount(o.startingCash) },
    { label: "Zeitlimit", value: o.timeLimitMinutes === 0 ? "Aus" : `${o.timeLimitMinutes} Minuten` },
    { label: "Hausregeln", value: rules.length === 0 ? "Keine" : rules.join(", ") },
  ];
}
